import React from "react";
import {
  ComponentLoader,
  PageTitle,
  StatsCard,
  SubscriberStatus,
} from "../../components";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { ShortLeft, Block } from "@tiny/icons";
import { useConfigView } from "../../Config.context";
import ContactHistoryTable from "./ContactHistoryTable/ContactHistoryTable";
import EngagementFunnelPanel from "../../customComponents/EnagementFunnelPanel";
import { useCustomerHistory } from "./CustomerHistory.hooks";
import TinyDateRangePicker from "../../components/TinyDateRangePicker";
import CustomerActivitiesPanel from "../../customComponents/CustomerActivityPanel";
import AudienceMembershipPanel from "../../customComponents/AudienceMembershipPanel";
import { defaultStaticRanges } from "./CustomerHistory.helpers";
import { addDays, startOfDay } from "date-fns";

function CustomerHistory() {
  const { config } = useConfigView();
  const {
    selectedRange,
    handleDateChange,
    recentActivityStats,
    customerDetails,
    isOverviewLoading,
    customerEmail,
    isSuppressed,
  } = useCustomerHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasBlacklistedOrBounced = customerDetails.some(
    (d) => d.title === "Blacklisted / Bounced" && d.value !== "N/A"
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else if (config?.navigateTo) {
      config.navigateTo("/main/audience");
    } else {
      window.history.back();
    }
  };

  if (isOverviewLoading) {
    return <ComponentLoader height={"100vh"} />;
  }

  return (
    <Box
      sx={{
        marginTop: {
          xs: config?.appName === "tinyEmail" ? 4 : 1,
          md: "auto",
        },
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <Button
          onClick={handleBack}
          startIcon={
            <ShortLeft
              sx={{
                transition: "transform 0.2s ease-in-out",
              }}
            />
          }
          sx={{
            color: "grey.100",
            textTransform: "none",
            fontWeight: "medium",
            fontSize: "14px",
            padding: 0,
            minWidth: "auto",
            "&:hover": {
              backgroundColor: "transparent",
              color: "primary.main",
              "& svg": {
                transform: "translateX(-4px)",
              },
            },
          }}
        >
          Go back
        </Button>
      </Box>
      <PageTitle
        title={
          config?.appName === "tinyEmail" ? (
            <Typography variant="h1" m={0} color="grey.100" p={0} noWrap>
              Contact History
            </Typography>
          ) : (
            <Typography variant="h2" m={0} color="grey.100" p={0} noWrap>
              Contact History
            </Typography>
          )
        }
      />

      <Grid container mb={4}>
        <Grid item xs={12}>
          <Typography variant="h3" color="grey.100" fontWeight="medium">
            Customer Details
          </Typography>
        </Grid>
      </Grid>

      <Grid
        container
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "0.5px solid #DFE0E5",
        }}
      >
        {customerDetails
          .filter((detail) =>
            hasBlacklistedOrBounced
              ? true
              : detail.title !== "Blacklisted / Bounced"
          )
          .map((detail) => (
            <Grid
              item
              xs={12}
              sm={hasBlacklistedOrBounced ? 2.4 : 6}
              md={hasBlacklistedOrBounced ? 2.4 : 4}
              lg={hasBlacklistedOrBounced ? 2.4 : 3}
              key={detail.title}
              p={{ xs: 4, lg: 6 }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {detail.title === "Status" ? (
                  <>
                    <SubscriberStatus status={detail?.value} />
                    {isSuppressed && (
                      <Box
                        component="span"
                        sx={(t) => ({
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          height: 20,
                          px: 2,
                          borderRadius: 9999,
                          backgroundColor: t.palette.red[50],
                          color: t.palette.error.main,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.02em",
                        })}
                      >
                        <Block sx={{ fontSize: 12 }} /> Suppressed
                      </Box>
                    )}
                  </>
                ) : detail.title === "Blacklisted / Bounced" ? (
                  <Typography
                    variant="h4"
                    color="error.main"
                    fontWeight="medium"
                  >
                    {detail.value || "N/A"}
                  </Typography>
                ) : (
                  <Typography variant="h4" color="grey.100" fontWeight="medium">
                    {detail.value || "N/A"}
                  </Typography>
                )}
              </Box>

              <Typography variant="label2R" color="grey.90">
                {detail.title}
              </Typography>
            </Grid>
          ))}
      </Grid>

      <Grid container mt={8} mb={4}>
        <Grid item xs={12}>
          <Typography variant="h3" color="grey.100" fontWeight="medium">
            Customer Activity
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={6}>
        {recentActivityStats.map((stat) => (
          <Grid item xs={6} md={4} key={stat.title}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container mb={6} mt={8}>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent={{ xs: "center", md: "flex-end" }}
        >
          <TinyDateRangePicker
            selectedRange={selectedRange}
            onChange={handleDateChange}
            maxDate={new Date()}
            minDate={startOfDay(addDays(new Date(), -180))}
            staticRanges={defaultStaticRanges}
          />
        </Grid>
      </Grid>

      <Box mt={8}>
        <EngagementFunnelPanel
          from={selectedRange?.startDate?.getTime()}
          to={selectedRange?.endDate?.getTime()}
          groupTimeUnit={"DAY"}
          email={customerEmail}
          title="Email Engagement"
        />
      </Box>

      <Grid container mt={8} mb={4}>
        <Grid item xs={12}>
          <CustomerActivitiesPanel email={customerEmail} />
        </Grid>
      </Grid>

      <Grid container mt={8} mb={4}>
        <Grid item xs={12}>
          <AudienceMembershipPanel email={customerEmail} />
        </Grid>
      </Grid>

      <Box mt={8}>
        <ContactHistoryTable
          filters={{
            from: selectedRange?.startDate?.getTime(),
            to: selectedRange?.endDate?.getTime(),
            email: customerEmail,
          }}
        />
      </Box>
    </Box>
  );
}

export default CustomerHistory;
